/**
 * ExternalSkillReviewPanel.jsx — the operator's way into the external-Skill review pipeline.
 *
 * This is a REVIEW-ONLY surface, and the shape of it says so. It has exactly two controls: one that
 * asks the server to review a candidate, and one that closes the card. There is no install, no
 * enable, no approve, no adopt and no "add to registry", because §3's chain ends this review at
 * EVALUATING and the registry's own human-approved door is the only thing that moves a Skill past it
 * (`Skill != Capability != Tool != Connection != Runtime`).
 *
 * Two rules make that structural rather than a promise:
 *
 *   1. **Every closed set comes from the server.** The source forms, the stages, the verdicts, the
 *      rejection reasons and their §3 precedence order, the §3 store-at-minimum facets and the two
 *      regexes are all read from `GET /api/tech-scout/skill-review/contract` and rendered from that
 *      answer. This file holds no option list of its own — `skillReviewVocabulary` supplies the
 *      Hebrew for each token and nothing else. A hardcoded list here would keep offering an arm the
 *      pipeline has renamed, and on THIS surface the stale arm would be a rejection reason.
 *
 *   2. **`declaration_incomplete` is not a rejection.** When the source layer cannot be built the
 *      server answers with no report and names the fields it needs; the card prints those fields and
 *      says the gate could not be asked. It never prints a verdict word in that state, because a
 *      verdict is a fact about the candidate and this state is a fact about the form.
 *
 * The authority block travels on every answer and is printed in BOTH states: "the review grants
 * nothing" is true whether or not a verdict was reached, and a card that only denied authority after
 * a successful review would be silent exactly where an operator is most likely to assume one.
 */

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { techScoutApi } from "../services/api";
import {
  SKILL_REVIEW_OUT_OF_SCOPE_NOTE_HE,
  skillReviewAuthoritySentences,
  skillReviewFieldLabel,
  skillReviewOutOfScopeLabel,
  skillReviewProblemLabels,
  skillReviewReasonLabels,
  skillReviewSourceFormLabel,
  skillReviewStageTrail,
  skillReviewStateLabel,
  skillReviewVerdictLabel,
} from "./skillReviewVocabulary";

const STATE_TONE = {
  reviewed: "bg-blue-50 text-blue-700 border-blue-200",
  declaration_incomplete: "bg-amber-50 text-amber-800 border-amber-300",
};

const VERDICT_TONE = {
  admitted: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-red-50 text-red-600 border-red-200",
};

function Chip({ children, tone }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${tone}`}>{children}</span>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-gray-400 shrink-0 min-w-36">{label}</span>
      <span className={`text-gray-700 break-all ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

/** One labelled text input. Blank is sent as absent, never as an empty claim. */
function Field({ field, value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className="block text-xs text-gray-500 mb-0.5">{skillReviewFieldLabel(field)}</span>
      <input
        type="text"
        dir="ltr"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(field, e.target.value)}
        className="w-full border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
        data-testid={`skill-review-field-${field}`}
      />
    </label>
  );
}

/**
 * The declaration form, built ENTIRELY from the contract's `declaration_fields` block.
 *
 * `source_form` is the one field whose value is a closed vocabulary, so it is a select over the
 * contract's own `source_forms`; `declared_tools` is a list, so it is a textarea with one reference
 * per line. Everything else is a bounded text input. The record-sourced half is deliberately absent:
 * those values come from the discovery record the review is bound to, and inviting an operator to
 * retype them would be inviting a value that contradicts that record.
 */
function DeclarationForm({ contract, declaration, onChange }) {
  const fields = contract?.declaration_fields || {};
  const forms = Array.isArray(contract?.source_forms) ? contract.source_forms : [];

  return (
    <div className="space-y-4" data-testid="skill-review-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="block text-xs text-gray-500 mb-0.5">
            {skillReviewFieldLabel("source_form")}
          </span>
          <select
            dir="ltr"
            value={declaration.source_form || ""}
            onChange={(e) => onChange("source_form", e.target.value)}
            className="w-full border rounded-lg px-2 py-1.5 text-sm bg-white"
            data-testid="skill-review-field-source_form"
          >
            <option value="">—</option>
            {forms.map((form) => (
              <option key={form.token} value={form.token}>
                {skillReviewSourceFormLabel(form.token)}
              </option>
            ))}
          </select>
        </label>
        {(fields.source || [])
          .filter((field) => field !== "source_form")
          .map((field) => (
            <Field
              key={field}
              field={field}
              value={declaration[field] || ""}
              onChange={onChange}
            />
          ))}
      </div>

      <fieldset className="border rounded-lg p-3 space-y-2">
        <legend className="text-xs text-gray-500 px-1">
          {skillReviewFieldLabel("attested_by")} — רשות. מילוי השדה הזה הופך את שאר שדות
          ההצמדה לחובה.
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(fields.attestation || []).map((field) => (
            <Field
              key={field}
              field={field}
              value={declaration[field] || ""}
              onChange={onChange}
            />
          ))}
        </div>
        <p className="text-xs text-gray-400">
          השדות שהאישור חייב להצמיד: {(fields.attestation_binding || [])
            .map((field) => skillReviewFieldLabel(field))
            .join(" · ")}
        </p>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {(fields.entry_from_review_request || [])
          .filter((field) => field !== "declared_tools")
          .map((field) => (
            <Field
              key={field}
              field={field}
              value={declaration[field] || ""}
              onChange={onChange}
            />
          ))}
      </div>

      <label className="block">
        <span className="block text-xs text-gray-500 mb-0.5">
          {skillReviewFieldLabel("declared_tools")} — שורה לכל מזהה
          {fields.declared_tools_max ? ` (עד ${fields.declared_tools_max})` : ""}
        </span>
        <textarea
          dir="ltr"
          rows={3}
          value={declaration.declared_tools || ""}
          onChange={(e) => onChange("declared_tools", e.target.value)}
          className="w-full border rounded-lg px-2 py-1.5 text-sm font-mono"
          data-testid="skill-review-field-declared_tools"
        />
      </label>
    </div>
  );
}

/**
 * The answer, rendered. Exported so it can be tested on a real server answer without a network call:
 * every acceptance-relevant claim on this surface is decided here.
 */
export function SkillReviewAnswer({ answer, contract }) {
  if (!answer) return null;
  const report = answer.report;
  const trust = answer.trust_evidence || {};
  const problems = skillReviewProblemLabels(answer.declaration_problems);
  const stages = Array.isArray(contract?.stages) ? contract.stages : [];
  const reasons = report ? skillReviewReasonLabels(report.rejection_reasons) : [];

  return (
    <div className="space-y-3" data-testid="skill-review-answer">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-800">תוצאת הבדיקה</span>
        <Chip tone={STATE_TONE[answer.state] || "bg-gray-50 text-gray-500 border-gray-200"}>
          {skillReviewStateLabel(answer.state)}
        </Chip>
        {report && (
          <Chip tone={VERDICT_TONE[report.verdict] || "bg-gray-50 text-gray-500 border-gray-200"}>
            {skillReviewVerdictLabel(report.verdict)}
          </Chip>
        )}
        {answer.read_only && (
          <Chip tone="bg-gray-50 text-gray-500 border-gray-200">קריאה בלבד</Chip>
        )}
        {answer.advisory_only && (
          <Chip tone="bg-gray-50 text-gray-500 border-gray-200">המלצתי בלבד</Chip>
        )}
      </div>

      {problems.length > 0 && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900"
          data-testid="skill-review-problems"
        >
          לא התקבלה הכרעה: החוזה לא הצליח לבנות את שכבת המקור. יש להשלים את השדות האלה ולשלוח שוב.
          <ul className="mt-1 list-disc list-inside">
            {problems.map((problem) => (
              <li key={problem.value} data-testid={`skill-review-problem-${problem.value}`}>
                {problem.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report && (
        <>
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2" data-testid="skill-review-stages">
              {skillReviewStageTrail(stages, report.stage).map((step) => (
                <span
                  key={step.stage}
                  data-testid={`skill-review-stage-${step.stage}`}
                  data-reached={step.reached ? "yes" : "no"}
                  className={`text-xs px-2 py-0.5 rounded-full border ${
                    step.current
                      ? "bg-brand-600 text-white border-brand-600"
                      : step.reached
                        ? "bg-brand-50 text-brand-700 border-brand-200"
                        : "bg-white text-gray-400 border-gray-200"
                  }`}
                >
                  {step.label}
                </span>
              ))}
            </div>
            {reasons.length > 0 && (
              <div className="text-xs text-gray-600" data-testid="skill-review-reasons">
                <span className="text-gray-400">סיבות, לפי סדר הקדימות של §3: </span>
                <ul className="mt-1 list-disc list-inside">
                  {reasons.map((reason, index) => (
                    <li key={report.rejection_reasons[index]}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}
            {report.reason && (
              <p className="text-xs text-gray-500">
                הסיבה המובילה: {skillReviewReasonLabels([report.reason])[0]}
              </p>
            )}
          </div>

          <div className="border rounded-lg p-3 space-y-1" data-testid="skill-review-report">
            <Row label="מפתח המיומנות" value={report.skill_key || "—"} mono />
            <Row label={skillReviewFieldLabel("entry_key")} value={report.entry_key || "—"} mono />
            <Row label={skillReviewFieldLabel("name")} value={report.name || "—"} />
            <Row
              label="תחום"
              value={`${report.intended_domain || "—"}${report.subdomain ? ` / ${report.subdomain}` : ""}`}
            />
            <Row
              label="נקודות §3 שנרשמו"
              value={
                Array.isArray(report.recorded_facets) && report.recorded_facets.length
                  ? report.recorded_facets.join(", ")
                  : "—"
              }
            />
            <Row
              label="נקודות §3 שחסרות"
              value={
                Array.isArray(report.missing_facets) && report.missing_facets.length
                  ? report.missing_facets.join(", ")
                  : "—"
              }
            />
            <Row label="טביעת הדוח" value={report.report_digest} mono />
          </div>

          <div className="border rounded-lg p-3 space-y-1" data-testid="skill-review-metadata">
            <div className="text-xs font-semibold text-gray-500">שדות ה-§3 שנרשמו</div>
            {Object.entries(report.metadata || {}).map(([field, value]) => (
              <Row key={field} label={skillReviewFieldLabel(field)} value={value || "—"} />
            ))}
            {Array.isArray(report.declared_tools) && report.declared_tools.length > 0 && (
              <Row
                label={skillReviewFieldLabel("declared_tools")}
                value={report.declared_tools.join(" · ")}
                mono
              />
            )}
          </div>

          <div className="border rounded-lg p-3 space-y-1" data-testid="skill-review-provenance">
            <div className="text-xs font-semibold text-gray-500">מקור</div>
            <Row label={skillReviewFieldLabel("source_id")} value={report.provenance.source_id} mono />
            <Row
              label={skillReviewFieldLabel("source_form")}
              value={skillReviewSourceFormLabel(report.provenance.source_origin)}
            />
            <Row
              label={skillReviewFieldLabel("source_locator")}
              value={report.provenance.source_locator}
              mono
            />
            <Row
              label={skillReviewFieldLabel("source_revision")}
              value={report.provenance.source_revision}
              mono
            />
            <Row
              label={skillReviewFieldLabel("discovered_by")}
              value={report.provenance.discovered_by}
            />
            <Row
              label={skillReviewFieldLabel("discovered_at")}
              value={report.provenance.discovered_at}
            />
            <Row label="נבדק במועד" value={report.provenance.reviewed_at} />
          </div>
        </>
      )}

      <div className="border rounded-lg p-3 space-y-1" data-testid="skill-review-trust">
        <div className="text-xs font-semibold text-gray-500">
          הראיות שהבדיקה נשענה עליהן
        </div>
        <Row
          label="הוצג אישור מקור"
          value={trust.source_attestation_presented ? "כן" : "לא"}
        />
        {/* A constant False on the wire: the pipeline establishes that an attestation EXISTS and who
            made it, not that it is valid. Printed as its own line so the card never reads as though a
            signature had been verified. */}
        <Row
          label="אותנטיות האישור אומתה"
          value={trust.attestation_authenticity_verified ? "כן" : "לא"}
        />
        <Row label="אושר על ידי" value={trust.attested_by || "—"} />
        <Row label="בתוקף עד" value={trust.attestation_expires_at || "—"} />
        <Row label="בוטל" value={trust.attestation_revoked ? "כן" : "לא"} />
        <Row label="הוצגה חתימת מועמד" value={trust.candidate_signature_presented ? "כן" : "לא"} />
        <Row label="המפרסם" value={trust.candidate_signer || "—"} />
        <Row
          label="הפניות לכלים שנמסרו"
          value={`${trust.declared_tool_reference_count ?? 0}`}
        />
        {Array.isArray(trust.declared_tool_references) &&
          trust.declared_tool_references.length > 0 && (
            <Row
              label="הפניות שנקראו"
              value={trust.declared_tool_references.join(" · ")}
              mono
            />
          )}
      </div>

      {/* Printed in BOTH states, on every answer, from the server's own block. */}
      <div
        className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600"
        data-testid="skill-review-authority"
      >
        <div className="font-semibold text-gray-500 mb-1">מה הבדיקה הזו לא עשתה</div>
        <ul className="list-disc list-inside space-y-0.5">
          {skillReviewAuthoritySentences(answer.authority).map((sentence) => (
            <li key={sentence}>{sentence}</li>
          ))}
        </ul>
      </div>

      <div className="text-xs text-gray-500" data-testid="skill-review-out-of-scope">
        {SKILL_REVIEW_OUT_OF_SCOPE_NOTE_HE}{" "}
        {Array.isArray(contract?.activation_statuses_out_of_scope) &&
          contract.activation_statuses_out_of_scope.map((status) => (
            <span key={status} className="inline-block mr-2">
              {skillReviewOutOfScopeLabel(status)}
            </span>
          ))}
      </div>

      {answer.spec && (
        <div className="text-xs text-gray-400" data-testid="skill-review-spec">
          {answer.spec.master_flow_ref} · {answer.spec.chain}
        </div>
      )}
    </div>
  );
}

/**
 * The panel for ONE discovery row.
 *
 * The contract is fetched by the panel and only when it is mounted, so the discovery list does not
 * pay for a request the operator has not asked for. A contract that fails to load stops the surface
 * rather than falling back to a local option list — a form built from a guess is exactly the failure
 * the contract endpoint exists to prevent.
 */
export default function ExternalSkillReviewPanel({ discovery, onClose }) {
  // The one source-layer value the discovery row already holds. `url` and `source_locator` are the
  // same fact under two names, so it is prefilled rather than retyped — and it is visible in its own
  // field, so an operator can replace it. Nothing else is prefilled: every other provenance field is
  // a claim about where a candidate came from, and this surface does not get to invent one.
  const [declaration, setDeclaration] = useState(() =>
    discovery?.url ? { source_locator: discovery.url } : {}
  );
  const [answer, setAnswer] = useState(null);

  const contractQuery = useQuery({
    queryKey: ["skill-review-contract"],
    queryFn: techScoutApi.skillReviewContract,
    staleTime: Infinity,
  });

  const reviewMutation = useMutation({
    mutationFn: (payload) => techScoutApi.reviewSkillCandidate(discovery.id, payload),
    onSuccess: (data) => setAnswer(data),
  });

  function change(field, value) {
    setDeclaration((current) => ({ ...current, [field]: value }));
  }

  function submit() {
    const payload = {};
    for (const [field, value] of Object.entries(declaration)) {
      if (field === "declared_tools") {
        const tools = String(value || "")
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean);
        if (tools.length) payload.declared_tools = tools;
      } else if (String(value || "").trim()) {
        payload[field] = value;
      }
    }
    setAnswer(null);
    reviewMutation.mutate(payload);
  }

  const contract = contractQuery.data;

  return (
    <div
      className="border-2 border-brand-200 rounded-xl p-4 space-y-4 bg-brand-50/30"
      dir="rtl"
      data-testid="skill-review-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-800">בדיקת מועמד חיצוני לפני קבלה</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            הבדיקה מריצה את שרשרת הקבלה של §3 על המועמד ומחזירה ראיות מקור והצמדה. היא אינה מתקינה,
            אינה מפעילה ואינה מעניקה הרשאה.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-3 py-1 rounded-full border border-gray-300 text-gray-600 shrink-0"
        >
          סגור
        </button>
      </div>

      {discovery && (
        <div className="text-xs text-gray-500" data-testid="skill-review-target">
          נבדק: <span className="font-medium text-gray-700">{discovery.technology_name}</span>
        </div>
      )}

      {contractQuery.isLoading && <p className="text-sm text-gray-400">טוען את החוזה…</p>}
      {contractQuery.isError && (
        <p role="alert" className="text-sm text-red-600" data-testid="skill-review-contract-error">
          לא ניתן לטעון את חוזה הבדיקה — אין טופס לנבנות מניחוש.
        </p>
      )}

      {contract && (
        <>
          <DeclarationForm contract={contract} declaration={declaration} onChange={change} />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={reviewMutation.isPending}
              className="bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg font-medium disabled:opacity-50"
            >
              {reviewMutation.isPending ? "בודק…" : "בדוק את המועמד"}
            </button>
            <span className="text-xs text-gray-400">
              שליחה מריצה בדיקה בלבד; דבר אינו נשמר ודבר אינו מאושר.
            </span>
          </div>
        </>
      )}

      {reviewMutation.isError && (
        <p role="alert" className="text-sm text-red-600" data-testid="skill-review-error">
          {reviewMutation.error?.response?.data?.detail || "שגיאה בהרצת הבדיקה."}
        </p>
      )}

      <SkillReviewAnswer answer={answer} contract={contract} />
    </div>
  );
}
